/**
 * BatchProgressCard — inline card the chat surface renders after the chat
 * agent batch-logs prior captures to a widget (Use Case C). The runner relays
 * `mcp_batch_log_to_widget`'s result as a `widget_batch_progress` card.
 *
 * Each row reflects one source session's outcome (logged / skipped duplicate),
 * resolved server-side by the `source_session_id` linkage on each
 * widget_user_data row — so the card is a faithful, idempotent record of the
 * batch. A ↗ jumps to the (possibly newly-instantiated) widget instance.
 *
 * Props:
 *   instanceId  — the target widget_instance_id (jump target).
 *   widgetTitle — display name.
 *   logged      — count of newly-logged rows.
 *   skipped     — count skipped as duplicates (re-run idempotency).
 *   results     — [{ source_session_id, status }].
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const STYLE = {
  card: {
    border: '1px solid #E5E5EA', borderRadius: 12, padding: '12px 14px',
    background: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 420,
  },
  header: { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13, color: '#1C1C1E' },
  title: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  jumpBtn: { border: 'none', background: 'transparent', color: '#3478F6', cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: 1 },
  summary: { fontSize: 12, fontWeight: 600, color: '#34C759' },
  rows: { display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' },
  row: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#3C3C43' },
  dotDone: { color: '#34C759' },
  dotSkip: { color: '#8E8E93' },
};

export default function BatchProgressCard({ instanceId, widgetTitle, logged = 0, skipped = 0, results = [] }) {
  const navigate = useNavigate();
  const onJump = useCallback(() => {
    if (instanceId) navigate(`/playground/widget/studio/instance/${instanceId}`);
  }, [navigate, instanceId]);

  const total = results.length || (logged + skipped);
  return (
    <div style={STYLE.card}>
      <div style={STYLE.header}>
        <span style={STYLE.title}>{widgetTitle || 'Batch log'}</span>
        {instanceId && (
          <button onClick={onJump} title="Open widget" aria-label="Open widget" style={STYLE.jumpBtn}>↗</button>
        )}
      </div>
      <div style={STYLE.summary}>
        ✓ Logged {logged}{skipped ? ` · ${skipped} already logged` : ''} · {total} session{total === 1 ? '' : 's'}
      </div>
      {results.length > 0 && (
        <div style={STYLE.rows}>
          {results.map((r, i) => {
            const done = r.status === 'logged';
            return (
              <div key={r.source_session_id || i} style={STYLE.row}>
                <span style={done ? STYLE.dotDone : STYLE.dotSkip}>{done ? '✓' : '○'}</span>
                <span>{done ? 'Logged' : 'Already logged'}</span>
                <span style={{ color: '#C7C7CC', fontFamily: 'monospace' }}>
                  {String(r.source_session_id || '').slice(0, 8)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
